import { normalizeFieldType } from "@/lib/fieldTypeCatalog";
import type { RuntimeFormField } from "@/lib/wartungen";

export type RuntimeScope = "masterdata" | "inspection";

export const DEFAULT_ENABLED_GRADES = [1, 2, 3, 4, 5, 6];

export const GRADE_COLORS: Record<number, string> = {
  1: "#16a34a",
  2: "#84cc16",
  3: "#f59e0b",
  4: "#f97316",
  5: "#dc2626",
  6: "#991b1b",
};

export interface NormalizedField extends RuntimeFormField {
  field_key: string;
  effective_scope: RuntimeScope;
  effective_field_type: string;
  effective_label: string;
  resolved_visible: boolean;
  resolved_enabled: boolean;
  resolved_required: boolean;
}

export interface FieldOption {
  value: string;
  label: string;
  grade_effect?: number | null;
  is_defect?: boolean;
}

export function normalizeScope(scope: unknown): RuntimeScope {
  return String(scope ?? "").trim().toLowerCase() === "masterdata" ? "masterdata" : "inspection";
}

export function getFieldKey(field: RuntimeFormField): string {
  return String(field?.field_key ?? field?.key ?? "").trim();
}

export function getFieldType(field: RuntimeFormField): string {
  const candidates = [
    field?.effective_field_type,
    field?.field_type,
    field?.type,
    (field?.ui as { type?: string; widget?: string })?.type,
    (field?.ui as { type?: string; widget?: string })?.widget,
  ];
  for (const candidate of candidates) {
    const mapped = normalizeFieldType(candidate);
    if (mapped) return mapped;
  }
  return "text";
}

export function getFieldLabel(field: RuntimeFormField): string {
  return String(
    (field?.runtime as { label?: string })?.label ??
      field?.effective_label ??
      field?.label_override ??
      field?.label ??
      getFieldKey(field) ??
      "Unbenanntes Feld",
  );
}

function getResolvedRequired(field: RuntimeFormField): boolean {
  const candidate =
    field?.resolved_required ??
    field?.required ??
    (field?.effective_options as { required?: boolean })?.required ??
    (field?.options_override_json as { required?: boolean })?.required ??
    (field?.options as { required?: boolean })?.required ??
    false;
  return Boolean(candidate);
}

export function normalizeFields(payload: {
  form_fields?: RuntimeFormField[];
  fields?: RuntimeFormField[];
  schema?: { fields?: RuntimeFormField[] };
}): NormalizedField[] {
  const direct = Array.isArray(payload?.form_fields)
    ? payload.form_fields
    : Array.isArray(payload?.fields)
      ? payload.fields
      : Array.isArray(payload?.schema?.fields)
        ? payload!.schema!.fields!
        : [];

  return direct
    .map((field) => {
      const fieldKey = getFieldKey(field);
      if (!fieldKey) return null;
      return {
        ...field,
        field_key: fieldKey,
        effective_scope: normalizeScope(field?.resolved_scope ?? field?.effective_scope ?? field?.scope),
        effective_field_type: getFieldType(field),
        effective_label: getFieldLabel(field),
        resolved_visible: Boolean(field?.resolved_visible ?? true),
        resolved_enabled: Boolean(field?.resolved_enabled ?? true),
        resolved_required: getResolvedRequired(field),
      } as NormalizedField;
    })
    .filter((f): f is NormalizedField => f != null);
}

export function getOptionsConfig(field: RuntimeFormField): Record<string, unknown> {
  const candidates = [field?.runtime, field?.effective_options, field?.options_override_json, field?.options];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      return candidate as Record<string, unknown>;
    }
  }
  return {};
}

export function getSelectOptions(field: RuntimeFormField): FieldOption[] {
  const eff = field?.effective_options;
  const opt = field?.options;
  const candidates: unknown[] = [
    (field?.runtime as { options?: unknown })?.options,
    (eff as { options?: unknown })?.options,
    (eff as { choices?: unknown })?.choices,
    (eff as { items?: unknown })?.items,
    Array.isArray(eff) ? eff : null,
    (opt as { options?: unknown })?.options,
    (opt as { choices?: unknown })?.choices,
    (opt as { items?: unknown })?.items,
    Array.isArray(opt) ? opt : null,
  ];
  let raw: unknown[] = [];
  for (const value of candidates) {
    if (Array.isArray(value)) {
      raw = value;
      break;
    }
  }
  return raw.map((entry) => {
    if (entry && typeof entry === "object") {
      const r = entry as Record<string, unknown>;
      const value = String(r.value ?? r.key ?? r.id ?? r.label ?? "").trim();
      const label = String(r.label ?? r.text ?? r.name ?? value).trim();
      const gradeEffect = r.grade_effect != null ? Number(r.grade_effect) : null;
      return {
        value,
        label: label || value,
        grade_effect: Number.isFinite(gradeEffect) ? gradeEffect : null,
        is_defect: Boolean(r.is_defect),
      } as FieldOption;
    }
    const v = String(entry ?? "").trim();
    return { value: v, label: v } as FieldOption;
  });
}

export function getAllowFreeInput(field: RuntimeFormField): { allow: boolean; label: string } {
  const config = getOptionsConfig(field);
  return {
    allow: Boolean(config?.allow_free_input),
    label: String(config?.free_input_label || "").trim() || "Eigener Wert",
  };
}

export function coerceGradeValue(raw: unknown): number | null {
  const value = Number.parseInt(String(raw ?? "").trim(), 10);
  return DEFAULT_ENABLED_GRADES.includes(value) ? value : null;
}

export function getGradeColor(grade: number | null): string {
  return grade != null ? GRADE_COLORS[grade] || "#6b7280" : "#6b7280";
}

/** Lesbare Schrift auf der Notenfarbe. Note 2 und 3 sind hell. */
export function getGradeForeground(grade: number | null): string {
  return grade === 2 || grade === 3 ? "#1c1917" : "#ffffff";
}

export function normalizeEnabledGrades(config: { enabled_grades?: number[] } | null | undefined): number[] {
  const list = Array.isArray(config?.enabled_grades) ? config!.enabled_grades! : [];
  const cleaned = Array.from(new Set(list.map((g) => Number(g)))).filter((g) => DEFAULT_ENABLED_GRADES.includes(g));
  cleaned.sort((a, b) => a - b);
  return cleaned.length ? cleaned : [...DEFAULT_ENABLED_GRADES];
}

export function isLayoutField(type: string): boolean {
  return type === "page_break" || type === "divider" || type === "static_text";
}

export function splitIntoPages(fields: NormalizedField[]): NormalizedField[][] {
  const pages: NormalizedField[][] = [];
  let current: NormalizedField[] = [];
  for (const field of fields) {
    if (field.effective_field_type === "page_break") {
      if (current.length) {
        pages.push(current);
        current = [];
      }
      continue;
    }
    current.push(field);
  }
  if (current.length) pages.push(current);
  return pages.length ? pages : [[]];
}

export function isValueEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function answerTokens(value: unknown): string[] {
  const normalize = (raw: unknown): string => {
    if (typeof raw === "boolean") return raw ? "true" : "false";
    return String(raw ?? "").trim().toLowerCase();
  };
  const list = Array.isArray(value) ? value.map(normalize) : [normalize(value)];
  return list.filter(Boolean);
}

function triggerGradeForField(field: NormalizedField, selected: string[]): number | null {
  const role = String(field.effective_semantic_role || "").trim().toLowerCase();
  if (role !== "defect") return null;
  const trigger = field.defect_trigger;
  if (!trigger || typeof trigger !== "object") return null;
  const record = trigger as { mode?: unknown; values?: unknown; grade_effect?: unknown };
  const grade = Number(record.grade_effect);
  if (!Number.isFinite(grade)) return null;
  const triggerValues = Array.isArray(record.values)
    ? record.values.map((entry) => answerTokens(entry)[0] || "").filter(Boolean)
    : [];
  const answers = selected.flatMap((entry) => answerTokens(entry));
  if (!triggerValues.length || !answers.length) return null;
  const triggerSet = new Set(triggerValues);
  const matched = answers.filter((entry) => triggerSet.has(entry));
  const mode = String(record.mode || "in").trim().toLowerCase();
  const triggered = mode === "equals" ? answers.length === 1 && matched.length > 0 : matched.length > 0;
  return triggered ? grade : null;
}

export interface FieldGradeContribution {
  grade: number;
  /** Antwort, die diese Note auslöst. */
  answerLabel: string;
  /** Mangel oder Note ab 2 – erklärt, warum das Objekt diese Note hat. */
  explainsGrade: boolean;
}

function selectedAnswerTokens(value: unknown): string[] {
  const selected = Array.isArray(value) ? value.map((entry) => String(entry)) : [String(value ?? "")];
  return selected.flatMap((entry) => answerTokens(entry));
}

/**
 * Note, die diese eine Antwort zur Gesamtnote beiträgt.
 * Die Gesamtnote ist das Maximum über alle Beiträge.
 */
export function fieldGradeContribution(field: NormalizedField, value: unknown): FieldGradeContribution | null {
  if (field.effective_scope === "masterdata") return null;
  if (!field.resolved_visible || !field.resolved_enabled) return null;

  const options = getSelectOptions(field);
  const selected = selectedAnswerTokens(value);
  let optionGrade: number | null = null;
  let answerLabel: string | null = null;
  let defectAnswer = false;

  for (const opt of options) {
    const optValue = opt.value.trim().toLowerCase();
    if (!optValue || !selected.includes(optValue)) continue;
    if (opt.is_defect) {
      defectAnswer = true;
      answerLabel = answerLabel || opt.label || opt.value;
    }
    if (opt.grade_effect == null) continue;
    if (optionGrade == null || opt.grade_effect > optionGrade) {
      optionGrade = opt.grade_effect;
      if (opt.grade_effect >= 2 || opt.is_defect) answerLabel = opt.label || opt.value;
    }
  }

  const triggerGrade = triggerGradeForField(field, selected);
  const rawGrade = optionGrade != null ? optionGrade : triggerGrade;
  const grade = coerceGradeValue(rawGrade);
  if (grade == null) return null;
  if (triggerGrade != null && optionGrade == null) {
    defectAnswer = true;
    answerLabel = answerLabel || formatFieldDisplayValue(field, value);
  }
  return {
    grade,
    answerLabel: answerLabel || formatFieldDisplayValue(field, value),
    explainsGrade: grade >= 2 || defectAnswer,
  };
}

/** Errechnet die automatische Note aus konfigurierten Notenwirkungen (max. Effekt zählt). */
export function computeAutoGrade(fields: NormalizedField[], values: Record<RuntimeScope, Record<string, unknown>>): number | null {
  let worst: number | null = null;
  for (const field of fields) {
    const contribution = fieldGradeContribution(field, values[field.effective_scope]?.[field.field_key]);
    if (!contribution) continue;
    worst = worst == null ? contribution.grade : Math.max(worst, contribution.grade);
  }
  return worst;
}

const CHECKED_TOKENS = new Set(["1", "true", "yes", "ja", "wahr"]);
const UNCHECKED_TOKENS = new Set(["0", "false", "no", "nein", "falsch"]);

function fieldTypeName(field: RuntimeFormField | null | undefined): string {
  return String(field?.effective_field_type || field?.field_type || field?.type || "")
    .trim()
    .toLowerCase();
}

export function isCheckboxField(field: RuntimeFormField | null | undefined): boolean {
  const type = fieldTypeName(field);
  return type === "checkbox" || type === "bool" || type === "boolean";
}

/** Ja/Nein so lesen, wie die Sichtbarkeitsregel `ist gleich true` sie vergleicht. */
export function isCheckedAnswer(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return CHECKED_TOKENS.has(value.trim().toLowerCase());
  return false;
}

function isUncheckedAnswer(value: unknown): boolean {
  if (value === false || value === 0) return true;
  if (typeof value === "string") return UNCHECKED_TOKENS.has(value.trim().toLowerCase());
  return false;
}

function booleanLabels(field: RuntimeFormField | null | undefined): { yes: string; no: string } {
  const ui = (field?.ui && typeof field.ui === "object" ? field.ui : {}) as Record<string, unknown>;
  const yes = String(ui.labelTrue ?? ui.label_true ?? "").trim() || "Ja";
  const no = String(ui.labelFalse ?? ui.label_false ?? "").trim() || "Nein";
  return { yes, no };
}

/** Zeigt den Antworttext einer Option, nicht den gespeicherten Schlüssel. */
export function formatFieldDisplayValue(field: RuntimeFormField | null | undefined, value: unknown): string {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    const parts = value.map((entry) => formatFieldDisplayValue(field, entry)).filter((part) => part !== "—");
    return parts.join(", ") || "—";
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const label = record.label ?? record.name ?? record.external_id;
    if (label != null && String(label).trim()) return String(label);
    return "—";
  }

  if (isCheckboxField(field) && (isCheckedAnswer(value) || isUncheckedAnswer(value))) {
    const labels = booleanLabels(field);
    return isCheckedAnswer(value) ? labels.yes : labels.no;
  }

  const raw = typeof value === "boolean" ? (value ? "true" : "false") : String(value).trim();
  if (!raw) return "—";
  if (field) {
    const token = raw.toLowerCase();
    const match = getSelectOptions(field).find(
      (opt) => opt.value.trim().toLowerCase() === token || opt.label.trim().toLowerCase() === token,
    );
    if (match) return match.label || match.value;
    if (typeof value === "boolean" || token === "true" || token === "false") {
      const labels = booleanLabels(field);
      return token === "true" ? labels.yes : labels.no;
    }
  }
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  return String(value);
}
